// World-effects toolkit step executors (Phase 0.3/0.4).
//
// Effects with an `id` PERSIST after their step's `duration` elapses — the
// duration only paces the timeline. They are torn down by `clear-effect` or
// automatically by ScenarioRunner.cleanup() → effects.removeAllEffects().
// This mirrors the existing `activate-sensor` grammar.
import { eventBus } from '../../core/events';
import type { StepRegistry } from '../StepRegistry';
import { wait } from './utils';

function resolveCoords(
  params: { coordinates: [number, number]; infraId?: string },
  systems: any,
): [number, number] {
  if (params.infraId && systems.city) {
    const pos = systems.city.getInfraPosition(params.infraId);
    if (pos) return pos;
  }
  return params.coordinates;
}

export function registerWorldEffectSteps(registry: StepRegistry): void {
  registry.register('geyser', async (step, systems) => {
    const id = step.params.id ?? 'geyser';
    systems.effects.startGeyser(id, resolveCoords(step.params, systems), {
      intensity: step.params.intensity,
      floodRadius: step.params.floodRadius,
    });
    await wait(step.duration);
  });

  registry.register('excavation', async (step, systems) => {
    const id = step.params.id ?? 'excavation';
    systems.effects.startExcavation(id, resolveCoords(step.params, systems), {
      radius: step.params.radius,
      durationMs: step.duration,
    });
    await wait(step.duration);
  });

  registry.register('pipe-lay', async (step, systems) => {
    const id = step.params.id ?? 'pipe-lay';
    systems.effects.startPipeLay(id, step.params.path, {
      color: step.params.color,
      durationMs: step.duration,
    });
    await wait(step.duration);
  });

  registry.register('flow-pulse', async (step, systems) => {
    const id = step.params.id ?? 'flow-pulse';
    systems.effects.startFlowPulse(id, step.params.path, {
      color: step.params.color,
      speed: step.params.speed,
    });
    await wait(step.duration);
  });

  registry.register('comms-broadcast', async (step, systems) => {
    const id = step.params.id ?? 'comms-broadcast';
    systems.effects.startCommsBroadcast(id, resolveCoords(step.params, systems), {
      color: step.params.color,
      radius: step.params.radius,
      targets: step.params.targets,
    });
    await wait(step.duration);
  });

  registry.register('multi-site-rag', async (step, systems) => {
    const id = step.params.id ?? 'multi-site-rag';
    const sites = step.params.sites.map((s: any) => ({
      coordinates: resolveCoords(s, systems),
      status: s.status,
    }));
    const linkTo = step.params.linkTo
      ? resolveCoords(step.params.linkTo, systems)
      : undefined;
    systems.effects.startMultiSiteRag(id, sites, linkTo);
    await wait(step.duration);
  });

  registry.register('color-grade', async (step) => {
    eventBus.emit('color-grade', step.params);
    await wait(step.duration);
  });

  registry.register('hud-metric', async (step) => {
    eventBus.emit('hud:metric', step.params);
    await wait(step.duration);
  });

  registry.register('clear-effect', async (step, systems) => {
    systems.effects.removeWorldEffect(step.params.id);
    eventBus.emit('hud:metric-clear', { id: step.params.id });
    await wait(step.duration);
  });

  registry.register('highlight-buildings', async (step, systems) => {
    const coords = resolveCoords(step.params, systems);
    const radius = step.params.radius ?? 80;
    systems.city?.highlightBuildingsNear(coords[0], coords[1], radius);
    await wait(step.duration);
  });

  // ── Combined-solution toolkit ──────────────────────────────────────────

  registry.register('convergence', async (step, systems) => {
    const id = step.params.id ?? 'convergence';
    const sources = step.params.sources.map((s: any) => resolveCoords(s, systems));
    const target = resolveCoords(step.params.target, systems);
    systems.effects.startConvergence(id, sources, target, {
      color: step.params.color,
      speed: step.params.speed,
    });
    await wait(step.duration);
  });

  registry.register('match-beam', async (step, systems) => {
    const id = step.params.id ?? 'match-beam';
    const hub = resolveCoords(step.params.hub, systems);
    const candidates = step.params.candidates.map((c: any) => ({
      coordinates: resolveCoords(c, systems),
      best: c.best,
    }));
    systems.effects.startMatchBeam(id, hub, candidates, {
      color: step.params.color,
      winColor: step.params.winColor,
      radius: step.params.radius,
      durationMs: step.duration,
      lockAt: step.params.lockAt,
    });
    await wait(step.duration);
  });

  registry.register('gauge-cluster', async (step, systems) => {
    const id = step.params.id ?? 'gauge-cluster';
    const coords = resolveCoords(step.params, systems);
    systems.effects.startGaugeCluster(id, coords, step.params.gauges, {
      durationMs: step.duration,
      spacing: step.params.spacing,
      gaugeRadius: step.params.gaugeRadius,
    });
    await wait(step.duration);
  });
}
